import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../hooks/useAuth';

const Sidebar = ({ isCollapsed = false, onToggle }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const navigationItems = [
    {
      section: 'Dashboard',
      items: [
        {
          label: 'Executive Dashboard',
          path: '/executive-dashboard',
          icon: 'BarChart3',
          description: 'Strategic overview and KPIs'
        },
        {
          label: 'Department Manager Hub',
          path: '/department-manager-hub',
          icon: 'Users',
          description: 'Team oversight and coordination'
        },
        {
          label: 'Staff Workflow Center',
          path: '/staff-workflow-center',
          icon: 'CheckSquare',
          description: 'Daily tasks and workflows'
        }
      ]
    },
    {
      section: 'Operations',
      items: [
        {
          label: 'Inventory Management',
          path: '/inventory-management',
          icon: 'Package',
          description: 'Stock control and tracking'
        }
      ]
    },
    {
      section: 'Financial',
      items: [
        {
          label: 'Financial Reports',
          path: '/financial-reports-center',
          icon: 'FileText',
          description: 'Reports and analytics'
        },
        {
          label: 'Budget Management',
          path: '/budget-management',
          icon: 'DollarSign',
          description: 'Budget planning and tracking'
        },
        {
          label: 'Financial Analytics',
          path: '/financial-analytics-dashboard',
          icon: 'TrendingUp',
          description: 'Financial trends and insights'
        }
      ]
    },
    {
      section: 'Administration',
      items: [
        {
          label: 'User Management',
          path: '/user-management',
          icon: 'UserCog',
          description: 'User accounts and permissions'
        }
      ]
    }
  ];

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const handleMobileToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      closeMobileMenu();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={handleMobileToggle}
        className="lg:hidden fixed top-4 left-4 z-300 p-2 rounded-md bg-card text-foreground sidebar-shadow"
        aria-label="Toggle navigation menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-250"
          onClick={closeMobileMenu}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-card border-r border-border sidebar-shadow z-100
          transition-smooth
          ${isCollapsed ? 'w-16' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo Section */}
        <div className="flex items-center h-16 px-4 border-b border-border">
          {!isCollapsed && (
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={20} color="white" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-semibold text-foreground">Ecomotech</span>
                <span className="text-xs text-muted-foreground">ERP System</span>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center mx-auto">
              <Icon name="Zap" size={20} color="white" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {navigationItems?.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-6">
              {!isCollapsed && (
                <h3 className="px-4 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {section?.section}
                </h3>
              )}
              <ul className="space-y-1 px-2">
                {section?.items?.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    <Link
                      to={item?.path}
                      onClick={closeMobileMenu}
                      className={`
                        flex items-center px-3 py-2 rounded-md text-sm font-medium transition-smooth
                        ${isActiveRoute(item?.path)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }
                        ${isCollapsed ? 'justify-center' : 'justify-start'}
                      `}
                      title={isCollapsed ? item?.label : ''}
                    >
                      <Icon 
                        name={item?.icon} 
                        size={20} 
                        className={isCollapsed ? '' : 'mr-3'} 
                      />
                      {!isCollapsed && (
                        <span className="truncate">{item?.label}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* User Profile Section */}
        <div className="p-4 border-t border-border">
          {!isCollapsed ? (
            <div className="mb-3">
              <div className="flex items-center space-x-3 px-3 py-2 rounded-md bg-muted">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={16} color="white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.role || 'Role'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-3">
              <div className="flex items-center justify-center px-3 py-2 rounded-md bg-muted">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
              </div>
            </div>
          )}
          
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center px-3 py-2 rounded-md text-sm font-medium 
              text-destructive hover:bg-destructive/10 transition-smooth
              ${isCollapsed ? 'justify-center' : 'justify-start'}
            `}
            title={isCollapsed ? 'Sign Out' : ''}
            aria-label="Sign out"
          >
            <Icon name="LogOut" size={20} className={isCollapsed ? '' : 'mr-3'} />
            {!isCollapsed && <span>Sign Out</span>}
          </button>
        </div>

        {/* Collapse Toggle (Desktop Only) */}
        {onToggle && (
          <div className="hidden lg:block p-4 border-t border-border">
            <button
              onClick={onToggle}
              className="w-full flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon 
                name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} 
                size={20} 
              />
              {!isCollapsed && <span className="ml-2">Collapse</span>}
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;